<template>
  <client-only>
    <template v-if="appStore.isMobileScreen">
      <bottom-drawer
        v-bind="_viewerProps"
        v-model:visible="isShown"
        :with-trigger-btn="props.withTriggerBtn"
        :title="props.title"
        :mask-closable="maskClosable"
        :display-directive="props.displayDirective"
        class="viewer-drawer"
        :drawer-content-props="{ class: props.viewerClass }"
        :is-footer-shown="props.isFooterShown"
        :z-index="props.zIndex"
        @close="handleModalCloseClick()"
        @after-enter="handleAfterEnter"
        @after-leave="handleAfterLeave"
      >
        <template #trigger="{ open }">
          <slot name="trigger" :open="open">
            <etg-button type="text" class="active" @click="handleTriggerClick(open)">{{ $t('general.view_all') }}</etg-button>
          </slot>
        </template>
        <template #default="{ close }">
          <div class="main-content" :class="[twMerge('px-5', props.mainContentClass), { 'full-height': props.isFullHeight }]">
            <slot name="default" :close="() => handleModalCloseClick(close)">
              <div :class="twMerge('py-5', props.bodyClass)">
                <component :is="props.body" v-if="isVNode(props.body)" />
                <template v-else>{{ props.body }}</template>
              </div>
            </slot>
          </div>
        </template>
        <template v-if="props.isFooterShown" #footer>
          <slot name="footer" :close="handleModalCloseClick">
            <div class="w-full py-3 px-5 flex justify-center gap-3">
              <etg-button v-if="!!props.cancelText" type="tertiary" :disabled="props.isCancelBtnDisabled" class="w-full" @click="handleCancelClick">
                {{ props.cancelText }}
              </etg-button>
              <etg-button
                type="primary"
                class="w-full"
                :disabled="props.isConfirmBtnDisabled"
                :loading="props.isConfirmBtnLoading"
                @click="handleConfirmClick"
              >
                {{ confirmText }}
              </etg-button>
            </div>
          </slot>
        </template>
      </bottom-drawer>
    </template>
    <template v-else>
      <slot v-if="props.withTriggerBtn" name="trigger" :open="handleTriggerClick">
        <etg-button type="text" class="active" @click="handleTriggerClick">{{ $t('general.view_all') }}</etg-button>
      </slot>
      <n-modal
        v-model:show="isShown"
        :display-directive="props.displayDirective"
        :z-index="props.zIndex"
        :mask-closable="maskClosable"
        :style="customCssVars"
        v-bind="_viewerProps"
        @mask-click="handleModalCloseClick()"
        @after-enter="handleAfterEnter"
        @after-leave="handleAfterLeave"
      >
        <div class="viewer-modal flex flex-col rounded mx-auto my-auto self-center" :class="twMerge('bg-white md:w-440px', viewerClass)">
          <div class="modal-header flex items-center justify-between gap-2 p-5 pb-2">
            <span class="title text-lg font-500">{{ props.title }}</span>
            <svg-icon name="cancel" class="cursor-pointer w-10 h-10 p-2 -mr-2" @click="handleModalCloseClick" />
          </div>
          <div
            class="main-content"
            :class="[twMerge('scrollbar pt-4 px-5 flex-1', props.mainContentClass), { 'full-height flex-none': props.isFullHeight }]"
          >
            <slot name="default" :close="handleModalCloseClick">
              <div :class="twMerge('pt-1 pb-10', props.bodyClass)">
                <component :is="props.body" v-if="isVNode(props.body)" />
                <template v-else>{{ props.body }}</template>
              </div>
            </slot>
          </div>
          <slot v-if="props.isFooterShown" name="footer" :close="handleModalCloseClick">
            <div class="content-viewer-footer w-full py-6 px-5 flex justify-end gap-2">
              <etg-button v-if="!!props.cancelText" type="tertiary" :disabled="props.isCancelBtnDisabled" @click="handleCancelClick">
                {{ props.cancelText }}
              </etg-button>
              <etg-button type="primary" :disabled="props.isConfirmBtnDisabled" :loading="props.isConfirmBtnLoading" @click="handleConfirmClick">
                {{ confirmText }}
              </etg-button>
            </div>
          </slot>
        </div>
        <template #footer>
          <slot name="footer" :close="handleModalCloseClick"></slot>
        </template>
      </n-modal>
    </template>
    <template v-if="props.isFallbackShown" #fallback>
      <slot name="trigger" :open="handleTriggerClick">
        <etg-button type="text" class="active">{{ $t('general.view_all') }}</etg-button>
      </slot>
    </template>
  </client-only>
</template>

<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import { NModal } from 'naive-ui'
import { useAppStore } from '@/store/app'

import BottomDrawer from '@/components/common/bottom-drawer.vue'

import { zIndex as zIndexConfig } from '@/theme.config'

import type { ModalCloseType, ModalState } from '@/types/commons'
import type { NModalProps, NDrawerProps } from '@/types/component'

interface Props extends Omit<ModalState, 'onClose'> {
  viewerClass?: string
  withTriggerBtn?: boolean
  viewerProps?: NModalProps
  isFallbackShown?: boolean
  displayDirective?: NDrawerProps['displayDirective']
  drawerBodyMaxHeight?: string | number
}

interface Emits {
  (event: 'close', type: ModalCloseType): void
  (event: 'after-enter'): void
  (event: 'after-leave'): void
}

const appStore = useAppStore()

const props = withDefaults(defineProps<Props>(), {
  title: '',
  confirmText: () => useI18n().t('general.confirm'),
  viewerClass: '',
  maskClosable: true,
  withTriggerBtn: true,
  isFooterShown: true,
  isFullHeight: false,
  isConfirmBtnDisabled: false,
  isCancelBtnDisabled: false,
  shouldCloseOnConfirm: true,
  shouldCloseOnCancel: true,
  zIndex: zIndexConfig.modal,
  displayDirective: 'if',
})

const emit = defineEmits<Emits>()

const isShown = defineModel<boolean>('show', { default: false })

const _viewerProps = computed(() => ({
  trapFocus: props.viewerProps?.trapFocus ?? false,
  ...props.viewerProps,
}))
const customCssVars = computed(() => {
  const MARGIN_Y = 160
  const TITLE_HEIGHT = 68
  const maxHeight = TITLE_HEIGHT + MARGIN_Y + (props.isFooterShown ? 88 : 0)

  return {
    '--modal-body-max-height': `calc(100dvh - ${maxHeight}px)`,
  }
})

const handleTriggerClick = (openFn?: () => void) => {
  if (typeof openFn === 'function') openFn()
  isShown.value = true
}

const handleModalCloseClick = (closeFn?: () => void) => {
  if (typeof closeFn === 'function') closeFn()
  handleClose('close')
}

const handleCancelClick = () => {
  props.onCancel?.()
  if (props.shouldCloseOnCancel) {
    handleClose('cancel')
  }
}

const handleConfirmClick = () => {
  props.onConfirm?.()
  if (props.shouldCloseOnConfirm) {
    isShown.value = false
  }
}

const handleClose = (type: ModalCloseType) => {
  isShown.value = false
  emit('close', type)
}

const handleAfterEnter = () => {
  emit('after-enter')
}

const handleAfterLeave = () => {
  emit('after-leave')
}
</script>

<style lang="scss" scoped>
.main-content {
  @screen md {
    max-height: var(--modal-body-max-height);

    &.full-height {
      height: var(--modal-body-max-height);
    }
  }
}

.content-viewer-footer {
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
}
</style>
